import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

type ServiceItem = {
  slug: string;
  en: string;
  es: string;
  icon: string;
  img: string;
  enDesc: string;
  esDesc: string;
};

type SubCategory = {
  labelEn: string;
  labelEs: string;
  icon: string;
  items: ServiceItem[];
};

type Category = {
  id: string;
  labelEn: string;
  labelEs: string;
  icon: string;
  color: string;
  bgGradient: string;
  accentColor: string;
  subcategories: SubCategory[];
};

const categories: Category[] = [
  {
    id: "handyman",
    labelEn: "Handyman Services",
    labelEs: "Servicios de Reparación",
    icon: "ri-tools-line",
    color: "text-brand-navy",
    bgGradient: "from-brand-navy to-slate-700",
    accentColor: "bg-brand-navy",
    subcategories: [
      {
        labelEn: "Basic Home Repairs",
        labelEs: "Reparaciones Básicas del Hogar",
        icon: "ri-home-gear-line",
        items: [
          {
            slug: "door-repairs",
            en: "Door Repairs",
            es: "Reparación de Puertas",
            icon: "ri-door-line",
            img: "https://readdy.ai/api/search-image?query=professional%20handyman%20man%20fixing%20door%20hinge%20lock%20alignment%20residential%20home%20interior%20bright%20Florida%20natural%20light%20focused%20skilled%20worker&width=600&height=400&seq=door_rep1&orientation=landscape",
            enDesc: "Hinges, locks, alignment — all door issues fixed fast.",
            esDesc: "Bisagras, cerraduras, alineación — todos los problemas de puertas.",
          },
          {
            slug: "cabinet-fixes",
            en: "Cabinet & Drawer Fixes",
            es: "Gabinetes y Cajones",
            icon: "ri-archive-line",
            img: "https://readdy.ai/api/search-image?query=handyman%20repairing%20kitchen%20cabinet%20drawer%20hinges%20home%20improvement%20professional%20Florida%20bright%20interior&width=600&height=400&seq=cab_fix1&orientation=landscape",
            enDesc: "Loose hinges, broken drawers, misaligned doors — all fixed.",
            esDesc: "Bisagras sueltas, cajones rotos — todo reparado.",
          },
          {
            slug: "baseboard-trim",
            en: "Baseboards & Trim",
            es: "Zócalos y Molduras",
            icon: "ri-layout-bottom-line",
            img: "https://readdy.ai/api/search-image?query=professional%20handyman%20installing%20replacing%20baseboards%20trim%20molding%20residential%20home%20interior%20bright%20clean%20Florida&width=600&height=400&seq=base_trim1&orientation=landscape",
            enDesc: "Replace or repair baseboards and trim for a polished finish.",
            esDesc: "Reemplaza o repara zócalos y molduras para un acabado impecable.",
          },
        ],
      },
      {
        labelEn: "Painting & Touch-Ups",
        labelEs: "Pintura y Retoques",
        icon: "ri-paint-brush-line",
        items: [
          {
            slug: "interior-painting",
            en: "Interior Painting",
            es: "Pintura Interior",
            icon: "ri-paint-brush-line",
            img: "https://readdy.ai/api/search-image?query=professional%20painter%20rolling%20white%20paint%20on%20wall%20bright%20clean%20room%20home%20improvement%20Florida%20residential%20natural%20light&width=600&height=400&seq=int_paint1&orientation=landscape",
            enDesc: "Fresh coats with perfect edges and a professional finish.",
            esDesc: "Capas frescas con bordes perfectos y acabado profesional.",
          },
          {
            slug: "patch-paint",
            en: "Patch & Paint Walls",
            es: "Parchar y Pintar Paredes",
            icon: "ri-brush-line",
            img: "https://readdy.ai/api/search-image?query=handyman%20patching%20wall%20hole%20spackle%20paint%20repair%20drywall%20residential%20home%20interior%20Florida%20professional&width=600&height=400&seq=patch_paint1&orientation=landscape",
            enDesc: "Patch holes, cracks, and imperfections — then paint over seamlessly.",
            esDesc: "Parcha hoyos, grietas e imperfecciones — luego pinta sin costuras.",
          },
          {
            slug: "tenant-touchups",
            en: "Touch-ups Between Tenants",
            es: "Retoques entre Inquilinos",
            icon: "ri-edit-box-line",
            img: "https://readdy.ai/api/search-image?query=professional%20painter%20doing%20touch%20up%20paint%20walls%20empty%20apartment%20between%20tenants%20rental%20property%20Florida%20bright&width=600&height=400&seq=tenant_touch1&orientation=landscape",
            enDesc: "Quick, precise touch-ups to refresh your rental between guests.",
            esDesc: "Retoques rápidos y precisos para refrescar tu propiedad entre inquilinos.",
          },
        ],
      },
      {
        labelEn: "Installation Services",
        labelEs: "Servicios de Instalación",
        icon: "ri-tv-line",
        items: [
          {
            slug: "tv-mounting",
            en: "TV Mounting",
            es: "Instalación de TV",
            icon: "ri-tv-line",
            img: "https://readdy.ai/api/search-image?query=professional%20handyman%20mounting%20flat%20screen%20TV%20on%20wall%20modern%20living%20room%20cable%20management%20Florida%20home&width=600&height=400&seq=tv_mount1&orientation=landscape",
            enDesc: "Safe, clean TV mounting with cable management included.",
            esDesc: "Instalación segura y limpia con manejo de cables incluido.",
          },
          {
            slug: "curtain-rods-blinds",
            en: "Curtain Rods & Blinds",
            es: "Cortinas y Persianas",
            icon: "ri-layout-top-line",
            img: "https://readdy.ai/api/search-image?query=handyman%20installing%20curtain%20rods%20window%20blinds%20residential%20home%20interior%20bright%20Florida%20professional%20service&width=600&height=400&seq=curtain1&orientation=landscape",
            enDesc: "Install curtain rods, blinds, and window treatments perfectly.",
            esDesc: "Instala barras de cortinas, persianas y tratamientos de ventanas.",
          },
          {
            slug: "shelves-wall-decor",
            en: "Shelves & Wall Decor",
            es: "Estantes y Decoración",
            icon: "ri-layout-grid-line",
            img: "https://readdy.ai/api/search-image?query=handyman%20installing%20floating%20shelves%20wall%20decor%20art%20frames%20residential%20home%20interior%20modern%20Florida%20bright&width=600&height=400&seq=shelves1&orientation=landscape",
            enDesc: "Floating shelves, wall art, and decor installed level and secure.",
            esDesc: "Estantes flotantes, arte y decoración instalados nivelados y seguros.",
          },
          {
            slug: "furniture-assembly",
            en: "Furniture Assembly",
            es: "Ensamblaje de Muebles",
            icon: "ri-sofa-line",
            img: "https://readdy.ai/api/search-image?query=professional%20handyman%20assembling%20IKEA%20furniture%20bedroom%20living%20room%20home%20service%20Florida%20natural%20light&width=600&height=400&seq=furn_asm1&orientation=landscape",
            enDesc: "All brands assembled quickly and correctly — IKEA and more.",
            esDesc: "Todas las marcas ensambladas rápido y correctamente — IKEA y más.",
          },
        ],
      },
      {
        labelEn: "Exterior & Maintenance",
        labelEs: "Exterior y Mantenimiento",
        icon: "ri-leaf-line",
        items: [
          {
            slug: "pressure-washing",
            en: "Pressure Washing",
            es: "Lavado a Presión",
            icon: "ri-drop-line",
            img: "https://readdy.ai/api/search-image?query=pressure%20washing%20driveway%20patio%20powerful%20water%20jet%20clean%20concrete%20outdoor%20Florida%20residential%20professional%20service&width=600&height=400&seq=press_wash1&orientation=landscape",
            enDesc: "Blast away dirt and grime from driveways, patios, and fences.",
            esDesc: "Elimina suciedad de entradas, patios y cercas con agua a presión.",
          },
          {
            slug: "gutter-cleaning",
            en: "Gutter Cleaning",
            es: "Limpieza de Canaletas",
            icon: "ri-water-flash-line",
            img: "https://readdy.ai/api/search-image?query=gutter%20cleaning%20professional%20removing%20leaves%20debris%20roof%20gutters%20residential%20home%20maintenance%20Florida%20ladder&width=600&height=400&seq=gutter1&orientation=landscape",
            enDesc: "Clear clogged gutters to protect your home from water damage.",
            esDesc: "Limpia canaletas obstruidas para proteger tu hogar del agua.",
          },
          {
            slug: "fence-repairs",
            en: "Fence Repairs",
            es: "Reparación de Cercas",
            icon: "ri-layout-grid-line",
            img: "https://readdy.ai/api/search-image?query=handyman%20repairing%20wooden%20fence%20residential%20backyard%20home%20improvement%20outdoor%20Florida%20sunshine%20professional&width=600&height=400&seq=fence1&orientation=landscape",
            enDesc: "Fix broken boards, leaning posts, and damaged fence sections.",
            esDesc: "Repara tablas rotas, postes inclinados y secciones dañadas.",
          },
          {
            slug: "landscaping",
            en: "Small Landscaping",
            es: "Jardinería Pequeña",
            icon: "ri-plant-line",
            img: "https://readdy.ai/api/search-image?query=small%20landscaping%20lawn%20care%20trimming%20plants%20residential%20yard%20beautiful%20green%20garden%20Florida%20sunshine%20professional&width=600&height=400&seq=landscape1&orientation=landscape",
            enDesc: "Lawn care, trimming, and small landscaping to keep your yard beautiful.",
            esDesc: "Cuidado del césped, poda y jardinería para un jardín hermoso.",
          },
        ],
      },
    ],
  },
  {
    id: "cleaning",
    labelEn: "Cleaning Services",
    labelEs: "Servicios de Limpieza",
    icon: "ri-sparkling-2-line",
    color: "text-brand-green",
    bgGradient: "from-emerald-500 to-teal-600",
    accentColor: "bg-brand-green",
    subcategories: [
      {
        labelEn: "All Cleaning Services",
        labelEs: "Todos los Servicios de Limpieza",
        icon: "ri-sparkling-2-line",
        items: [
          {
            slug: "residential-cleaning",
            en: "Residential Cleaning",
            es: "Limpieza Residencial",
            icon: "ri-home-smile-line",
            img: "https://readdy.ai/api/search-image?query=beautiful%20slim%20young%20woman%20professional%20cleaner%20green%20uniform%20smiling%20vacuuming%20bright%20modern%20living%20room%20Florida%20home%20sparkling%20clean%20warm%20natural%20light&width=600&height=400&seq=res_clean1&orientation=landscape",
            enDesc: "Regular maintenance cleaning to keep your home fresh and spotless.",
            esDesc: "Limpieza de mantenimiento regular para mantener tu hogar impecable.",
          },
          {
            slug: "deep-cleaning",
            en: "Deep Cleaning",
            es: "Limpieza Profunda",
            icon: "ri-sparkling-2-line",
            img: "https://readdy.ai/api/search-image?query=handsome%20professional%20man%20cleaner%20uniform%20scrubbing%20kitchen%20countertops%20deep%20cleaning%20gleaming%20surfaces%20bright%20modern%20Florida%20home%20natural%20light&width=600&height=400&seq=deep_clean1&orientation=landscape",
            enDesc: "Thorough top-to-bottom cleaning reaching every corner.",
            esDesc: "Limpieza exhaustiva de arriba a abajo llegando a cada rincón.",
          },
          {
            slug: "move-in-cleaning",
            en: "Move-in Cleaning",
            es: "Limpieza de Entrada",
            icon: "ri-door-open-line",
            img: "https://readdy.ai/api/search-image?query=beautiful%20slim%20young%20woman%20professional%20cleaner%20uniform%20mopping%20gleaming%20floors%20empty%20bright%20new%20apartment%20move%20in%20cleaning%20Florida%20cheerful%20smile&width=600&height=400&seq=movein1&orientation=landscape",
            enDesc: "Start fresh in your new home — spotless before you move in.",
            esDesc: "Empieza de cero en tu nuevo hogar — impecable antes de mudarte.",
          },
          {
            slug: "move-out-cleaning",
            en: "Move-out Cleaning",
            es: "Limpieza de Salida",
            icon: "ri-logout-box-r-line",
            img: "https://readdy.ai/api/search-image?query=professional%20man%20cleaner%20uniform%20wiping%20cleaning%20empty%20apartment%20move%20out%20spotless%20bright%20Florida%20home%20natural%20light%20focused&width=600&height=400&seq=moveout1&orientation=landscape",
            enDesc: "Get your full deposit back — we clean every inch.",
            esDesc: "Recupera tu depósito completo — limpiamos cada centímetro.",
          },
          {
            slug: "airbnb-cleaning",
            en: "Airbnb Cleaning",
            es: "Limpieza Airbnb",
            icon: "ri-building-4-line",
            img: "https://readdy.ai/api/search-image?query=beautiful%20slim%20young%20woman%20professional%20cleaner%20uniform%20making%20bed%20hotel%20style%20white%20linens%20bright%20modern%20bedroom%20Airbnb%20vacation%20rental%20Florida%20smiling&width=600&height=400&seq=airbnb1&orientation=landscape",
            enDesc: "Fast turnovers between guests — hotel-quality results.",
            esDesc: "Cambios rápidos entre huéspedes — resultados de calidad hotelera.",
          },
          {
            slug: "window-cleaning",
            en: "Window Cleaning",
            es: "Limpieza de Ventanas",
            icon: "ri-window-line",
            img: "https://readdy.ai/api/search-image?query=professional%20window%20cleaner%20man%20green%20uniform%20using%20squeegee%20sparkling%20crystal%20clear%20glass%20windows%20bright%20sunny%20Florida%20residential%20home%20exterior%20cheerful%20focused%20streak%20free&width=600&height=400&seq=win_clean1&orientation=landscape",
            enDesc: "Crystal-clear windows inside and out — streak-free guaranteed.",
            esDesc: "Ventanas cristalinas por dentro y por fuera — sin rayas, garantizado.",
          },
        ],
      },
    ],
  },
  {
    id: "odd-jobs",
    labelEn: "Odd Jobs",
    labelEs: "Trabajos Varios",
    icon: "ri-hammer-line",
    color: "text-amber-600",
    bgGradient: "from-amber-500 to-orange-500",
    accentColor: "bg-amber-500",
    subcategories: [
      {
        labelEn: "Quick Services",
        labelEs: "Servicios Rápidos",
        icon: "ri-hammer-line",
        items: [
          {
            slug: "house-cleaning-odd",
            en: "House Cleaning",
            es: "Limpieza del Hogar",
            icon: "ri-home-2-line",
            img: "https://readdy.ai/api/search-image?query=professional%20woman%20cleaner%20smiling%20cleaning%20bright%20modern%20house%20living%20room%20Florida%20natural%20light%20cheerful%20uniform&width=600&height=400&seq=house_odd1&orientation=landscape",
            enDesc: "One-time or occasional house cleaning for any need.",
            esDesc: "Limpieza del hogar puntual u ocasional para cualquier necesidad.",
          },
          {
            slug: "airbnb-cleaning-odd",
            en: "Airbnb Cleaning",
            es: "Limpieza Airbnb",
            icon: "ri-building-line",
            img: "https://readdy.ai/api/search-image?query=professional%20cleaner%20man%20woman%20team%20cleaning%20Airbnb%20vacation%20rental%20bright%20bedroom%20bathroom%20Florida%20hotel%20quality%20turnover&width=600&height=400&seq=airbnb_odd1&orientation=landscape",
            enDesc: "Quick, reliable Airbnb turnovers to keep your ratings high.",
            esDesc: "Cambios rápidos y confiables de Airbnb para mantener tus calificaciones.",
          },
          {
            slug: "handyman-small-repairs",
            en: "Handyman Small Repairs",
            es: "Reparaciones Pequeñas",
            icon: "ri-hammer-line",
            img: "https://readdy.ai/api/search-image?query=handyman%20doing%20small%20home%20repairs%20fixing%20various%20things%20around%20house%20Florida%20residential%20professional%20tools%20belt&width=600&height=400&seq=small_rep1&orientation=landscape",
            enDesc: "Small fixes around the house — quick, affordable, and reliable.",
            esDesc: "Pequeñas reparaciones en casa — rápidas, económicas y confiables.",
          },
        ],
      },
    ],
  },
];

const ServicesPage = () => {
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const [activeTab, setActiveTab] = useState("handyman");

  const activeCategory = categories.find((c) => c.id === activeTab)!;

  const ServiceCard = ({ item, accentColor }: { item: ServiceItem; accentColor: string }) => (
    <Link
      to={`/services/${item.slug}`}
      className="group bg-white rounded-2xl overflow-hidden cursor-pointer block border border-gray-100 hover:border-gray-200 transition-all hover:-translate-y-0.5 duration-200"
    >
      <div className="w-full h-56 overflow-hidden">
        <img
          src={item.img}
          alt={isEn ? item.en : item.es}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4">
        <div className={`w-8 h-8 flex items-center justify-center rounded-xl mb-2.5 ${accentColor}/10`}>
          <i className={`${item.icon} ${accentColor.replace("bg-", "text-")} text-base`}></i>
        </div>
        <h3 className="font-heading font-bold text-brand-dark text-sm mb-1">{isEn ? item.en : item.es}</h3>
        <p className="font-body text-gray-500 text-xs leading-relaxed mb-3">{isEn ? item.enDesc : item.esDesc}</p>
        <span className={`inline-flex items-center gap-1 text-xs font-heading font-semibold ${accentColor.replace("bg-", "text-")}`}>
          {isEn ? "Learn More" : "Saber Más"} <i className="ri-arrow-right-line"></i>
        </span>
      </div>
    </Link>
  );

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-36 pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20cleaning%20handyman%20service%20team%20working%20bright%20modern%20home%20Florida%20residential%20beautiful%20interior%20tools%20equipment&width=1920&height=600&seq=svhero2&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-brand-dark/75"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-6 text-center">
            <div className="flex items-center justify-center gap-2 text-white/60 text-sm font-body mb-4">
              <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white">{isEn ? "Services" : "Servicios"}</span>
            </div>
            <h1 className="font-heading font-black text-white text-4xl md:text-6xl mb-4">
              {isEn ? "Our Services" : "Nuestros Servicios"}
            </h1>
            <p className="font-body text-white/70 text-base md:text-lg max-w-xl mx-auto">
              {isEn
                ? "Handyman repairs, professional cleaning, and odd jobs — one team for everything."
                : "Reparaciones, limpieza profesional y trabajos varios — un equipo para todo."}
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 md:top-20 z-20 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-heading font-semibold text-sm whitespace-nowrap transition-all cursor-pointer ${
                    activeTab === cat.id
                      ? `${cat.accentColor} text-white`
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {isEn ? cat.labelEn : cat.labelEs}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Services Content */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">

            {/* Category Header */}
            <div className={`inline-flex items-center gap-3 bg-gradient-to-r ${activeCategory.bgGradient} text-white px-6 py-3 rounded-2xl mb-10`}>
              <i className={`${activeCategory.icon} text-xl`}></i>
              <span className="font-heading font-bold text-lg">
                {isEn ? activeCategory.labelEn : activeCategory.labelEs}
              </span>
            </div>

            {/* Subcategories */}
            {activeCategory.subcategories.map((sub) => (
              <div key={sub.labelEn} className="mb-12">
                {/* Subcategory Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${activeCategory.accentColor}/10`}>
                    <i className={`${sub.icon} ${activeCategory.color} text-lg`}></i>
                  </div>
                  <h2 className="font-heading font-bold text-brand-dark text-xl md:text-2xl">
                    {isEn ? sub.labelEn : sub.labelEs}
                  </h2>
                  <div className="flex-1 h-px bg-gray-100 ml-2"></div>
                </div>

                {/* Items Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {sub.items.map((item) => (
                    <ServiceCard key={item.slug} item={item} accentColor={activeCategory.accentColor} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-brand-beige">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-brand-dark mb-3">
              {isEn ? "Not sure which service you need?" : "¿No sabes qué servicio necesitas?"}
            </h2>
            <p className="font-body text-gray-500 text-base mb-6">
              {isEn ? "Contact us and we'll help you figure it out — free consultation!" : "Contáctanos y te ayudamos a decidir — ¡consulta gratis!"}
            </p>
            <a
              href="https://wa.me/15612626118"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-green-dark text-white px-7 py-3.5 rounded-full font-heading font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              {isEn ? "Chat on WhatsApp" : "Chatear por WhatsApp"}
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ServicesPage;
